import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import TabellaLista from '../../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { sportLabel } from '../../../../components/Util/sportsCatalog';
import { useFunzioni } from '../../../../components/Util/featureFlags';

import { SelettoreStato } from '../../../../components/Util/StatoProdotto';
/*
 * I traguardi (move-goals) di una disciplina: quelli che l'utente dell'app raggiunge
 * registrando attivita'. Il backend li legge dal database a ogni sincronizzazione, quindi
 * un traguardo salvato qui conta dalle sessioni successive.
 *
 * Regole che la pagina deve rendere evidenti:
 *  - finche' nessuno ha iniziato il traguardo si cambia tutto tranne la disciplina; dopo
 *    solo il testo: per il resto si disattiva e se ne crea uno nuovo, cosi' nessuno vede
 *    cambiare le condizioni mentre ci sta lavorando;
 *  - un traguardo che qualcuno ha gia' iniziato non si elimina, si disattiva: chi l'aveva
 *    iniziato continua a vederne la soglia nell'app;
 *  - con la gestione spenta (produzione) si cambia solo la descrizione.
 */

const GLOBAL = 'GLOBAL';

const getToken = () => {
    const details = sessionStorage.getItem('userDetails');
    if (!details) return null;
    return JSON.parse(details).idToken;
};

/*
 * Metriche dei traguardi. Passi e trofei in sport diversi valgono solo per i traguardi di
 * tutte le discipline (GLOBAL). Le sfide vinte arrivano dall'import ma il backend non le
 * calcola ancora (calculated: false): si mostrano ma non si propongono, perche' un traguardo
 * cosi' non si raggiungerebbe mai.
 *
 * factor: da unita' mostrata a unita' salvata (ore -> millisecondi, km -> metri).
 */
const METRICS = [
    { value: 'DURATION', label: 'Durata', unit: 'ore', factor: 3600000, step: '0.5', global: false },
    { value: 'DISTANCE', label: 'Distanza', unit: 'km', factor: 1000, step: '0.1', global: false },
    { value: 'HEIGHT_DIFFERENCE', label: 'Dislivello', unit: 'metri', factor: 1, step: '1', global: false },
    { value: 'SESSIONS', label: 'Sessioni', unit: 'sessioni', factor: 1, step: '1', global: false },
    { value: 'CHALLENGES_WON', label: 'Sfide vinte', unit: 'sfide', factor: 1, step: '1', global: false, calculated: false },
    { value: 'STEPS', label: 'Passi', unit: 'passi', factor: 1, step: '1', global: true },
    { value: 'BRONZE_TROPHIES_IN_DIFFERENT_SPORTS', label: 'Bronzi in sport diversi', unit: 'sport', factor: 1, step: '1', global: true },
    { value: 'SILVER_TROPHIES_IN_DIFFERENT_SPORTS', label: 'Argenti in sport diversi', unit: 'sport', factor: 1, step: '1', global: true },
    { value: 'GOLD_TROPHIES_IN_DIFFERENT_SPORTS', label: 'Ori in sport diversi', unit: 'sport', factor: 1, step: '1', global: true },
];

const PERIODS = [
    { value: 'DAY', label: 'Giorno' },
    { value: 'WEEK', label: 'Settimana' },
    { value: 'MONTH', label: 'Mese' },
    { value: 'TRIMESTER', label: 'Trimestre' },
    { value: 'SEMESTER', label: 'Semestre' },
    { value: 'YEAR', label: 'Anno' },
];

const TROPHIES = [
    { value: 'BRONZE', label: 'Bronzo' },
    { value: 'SILVER', label: 'Argento' },
    { value: 'GOLD', label: 'Oro' },
    { value: 'PLATINUM', label: 'Platino' },
];

const metricOf = (value) => METRICS.find((m) => m.value === value);
const labelOf = (list, value) => list.find((item) => item.value === value)?.label ?? value;

const toDisplayUnit = (metric, stored) => {
    const m = metricOf(metric);
    if (!m) return stored;
    return Math.round((stored / m.factor) * 100) / 100;
};

const formatThreshold = (metric, stored) => {
    const m = metricOf(metric);
    if (!m) return String(stored);
    return `${toDisplayUnit(metric, stored).toLocaleString('it-IT')} ${m.unit}`;
};

/*
 * Una proposta di descrizione, sullo stile di quelle gia' caricate ("Registra 15 ore di
 * attivita' Basket"). Si scrive solo finche' il Superadmin non tocca il campo.
 */
const suggestDescription = (metric, amount, sportName) => {
    if (!metric || !amount) return '';
    const where = sportName ? ` di attività ${sportName}` : '';
    switch (metric) {
        case 'DURATION':
            return `Registra ${amount} ore${where}`;
        case 'DISTANCE':
            return `Percorri ${amount} km${where}`;
        case 'HEIGHT_DIFFERENCE':
            return `Supera ${amount} metri di dislivello${where}`;
        case 'SESSIONS':
            return `Registra ${amount} sessioni${where}`;
        case 'CHALLENGES_WON':
            return `Vinci ${amount} sfide${sportName ? ` a ${sportName}` : ''}`;
        case 'STEPS':
            return `Fai ${amount} passi`;
        case 'BRONZE_TROPHIES_IN_DIFFERENT_SPORTS':
            return `Vinci un bronzo in ${amount} sport diversi`;
        case 'SILVER_TROPHIES_IN_DIFFERENT_SPORTS':
            return `Vinci un argento in ${amount} sport diversi`;
        case 'GOLD_TROPHIES_IN_DIFFERENT_SPORTS':
            return `Vinci un oro in ${amount} sport diversi`;
        default:
            return '';
    }
};

/*
 * Fitcoin per ogni trofeo: copia dei valori cablati nel backend (sportid-be,
 * libs/fitcoin/src/fitcoin-manager/fitcoin-manager.service.ts, trophyFitCoin). Chi ha
 * Premium ne prende 5 volte tanti. Se cambiano li', vanno cambiati anche qui.
 */
const TROPHY_FITCOIN = { BRONZE: 20, SILVER: 100, GOLD: 400, PLATINUM: 1000 };
const PREMIUM_FITCOIN_RATIO = 5;

const LegendaFitcoin = () => (
    <div className="sid-fitcoin-legenda">
        <span className="sid-fitcoin-legenda__titolo">Fitcoin per trofeo</span>
        {TROPHIES.map((t) => (
            <span key={t.value} className="sid-fitcoin-legenda__voce">
                <span className={`sid-fitcoin-legenda__medaglia is-${t.value.toLowerCase()}`}></span>
                <strong>{t.label}</strong>
                <span>{TROPHY_FITCOIN[t.value].toLocaleString('it-IT')}</span>
                <span className="sid-fitcoin-legenda__premium">
                    Premium {(TROPHY_FITCOIN[t.value] * PREMIUM_FITCOIN_RATIO).toLocaleString('it-IT')}
                </span>
            </span>
        ))}
    </div>
);

const STATO_OPTIONS = ['Attivo', 'Disattivato'];
const TROFEO_OPTIONS = TROPHIES.map((t) => t.label);

const EMPTY_FORM = {
    moveGoalId: null,
    description: '',
    descriptionTouched: false,
    metric: '',
    periodType: '',
    amount: '',
    trophyType: '',
};

/*
 * La tabella dei traguardi di una disciplina, con le sue finestre. Si usa nel tab
 * «Traguardi» della scheda disciplina (scheda.js) e, per i traguardi di tutte le
 * discipline (GLOBAL), nella pagina qui sotto.
 *
 * `onBack`: se c'e', in fondo compare la barra con Indietro.
 * Il titolo della tabella si mostra solo per GLOBAL: nel tab lo dice gia' il nome del tab.
 */
export const TraguardiTabella = ({ code, onBack }) => {
    // Interruttore «Gestione dei traguardi» (Impostazioni › Funzioni)
    const { moveGoalsManagement: MOVE_GOALS_MANAGEMENT_ENABLED } = useFunzioni();
    const isGlobal = code === GLOBAL;
    const sportName = isGlobal ? '' : sportLabel(code);
    const titolo = isGlobal ? 'Traguardi di tutte le discipline' : undefined;

    const [data_json, setDataJson] = useState(null);
    const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);
    const [trofeoFilter, setTrofeoFilter] = useState(TROFEO_OPTIONS);

    const filteredData = useMemo(
        () =>
            data_json &&
            data_json.filter(
                (goal) =>
                    filtroMultiplo(statoFilter, STATO_OPTIONS, goal.stato) &&
                    filtroMultiplo(trofeoFilter, TROFEO_OPTIONS, goal.trofeo),
            ),
        [data_json, statoFilter, trofeoFilter],
    );

    const [loadingShowed, setLoadingShowed] = useState(false);
    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    const [confirmShowed, setConfirmShowed] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [onContinue, setOnContinue] = useState(null);

    // Riquadro di modifica aperto sotto la riga: moveGoalId del traguardo, 'new' in testa alla tabella, null chiuso
    const [openId, setOpenId] = useState(null);
    const closePanel = () => setOpenId(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const isNew = form.moveGoalId === null;

    const showError = (text) => {
        setModalTitle("Errore durante l'operazione");
        setModalText(text);
        setModalShowed(true);
    };

    const loadData = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        setLoadingShowed(true);
        const result = await new MyApiClient().getAdminMoveGoals(token, code);
        setLoadingShowed(false);

        if (!result || result.error) {
            showError(result?.error ?? 'Impossibile leggere i traguardi della disciplina');
            setDataJson([]);
            return;
        }

        setDataJson(
            result.map((goal) => ({
                ...goal,
                metrica: labelOf(METRICS, goal.metric),
                periodo: labelOf(PERIODS, goal.periodType),
                soglia: formatThreshold(goal.metric, goal.threshold),
                trofeo: labelOf(TROPHIES, goal.trophyType),
                stato: goal.active ? 'Attivo' : 'Disattivato',
            })),
        );
    }, [code]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const updateForm = (changes) => {
        setForm((prev) => {
            const next = { ...prev, ...changes };
            if (next.moveGoalId === null && !next.descriptionTouched) {
                next.description = suggestDescription(next.metric, next.amount, sportName);
            }
            return next;
        });
    };

    const openNew = () => {
        setForm(EMPTY_FORM);
        setOpenId('new');
    };

    // Clic sulla riga: apre il riquadro sotto di lei, un secondo clic lo chiude
    const openEdit = (row) => {
        if (openId === row.moveGoalId) return closePanel();
        setForm({
            moveGoalId: row.moveGoalId,
            description: row.description,
            descriptionTouched: true,
            metric: row.metric,
            periodType: row.periodType,
            amount: String(toDisplayUnit(row.metric, row.threshold)),
            trophyType: row.trophyType,
            original: row,
        });
        setOpenId(row.moveGoalId);
    };

    const save = async () => {
        const description = form.description.trim();
        if (description.length < 3) return showError('Scrivi la descrizione che vedra\' l\'utente nell\'app.');

        const metric = metricOf(form.metric);
        const amount = Number(String(form.amount).replace(',', '.'));
        if (!lockedRule) {
            if (!metric) return showError('Scegli la metrica.');
            if (!form.periodType) return showError('Scegli il periodo.');
            if (!form.trophyType) return showError('Scegli il trofeo.');
            if (!Number.isFinite(amount) || amount <= 0) return showError('La soglia deve essere maggiore di zero.');
        }
        const threshold = metric ? Math.round(amount * metric.factor) : undefined;

        const token = getToken();
        if (!token) return;
        const apiClient = new MyApiClient();

        let body;
        if (isNew) {
            body = { description, metric: form.metric, periodType: form.periodType, threshold, trophyType: form.trophyType };
        } else {
            // Si mandano solo i campi cambiati: a gestione spenta il server accetta solo la descrizione
            body = {};
            if (description !== form.original.description) body.description = description;
            if (!lockedRule) {
                if (form.metric !== form.original.metric) body.metric = form.metric;
                if (form.periodType !== form.original.periodType) body.periodType = form.periodType;
                if (threshold !== form.original.threshold) body.threshold = threshold;
                if (form.trophyType !== form.original.trophyType) body.trophyType = form.trophyType;
            }
            if (Object.keys(body).length === 0) return closePanel();
        }

        setLoadingShowed(true);
        const result = isNew
            ? await apiClient.createAdminMoveGoal(token, code, body)
            : await apiClient.updateAdminMoveGoal(token, form.moveGoalId, body);
        setLoadingShowed(false);

        if (result && result.error) {
            if (result.status === 409 && /started/.test(result.error)) {
                // Qualcuno l'ha iniziato mentre la finestra era aperta
                await loadData();
                return showError('Nel frattempo qualcuno ha iniziato questo traguardo: ora si puo\' cambiare solo la descrizione.');
            }
            if (result.status === 409) {
                return showError('Esiste gia\' un traguardo con la stessa metrica, periodo, soglia e trofeo per questa disciplina.');
            }
            return showError(result.error);
        }
        closePanel();
        return loadData();
    };

    // Stato del traguardo accanto al titolo del riquadro: cambia e si salva subito (regola SUI 03/10/2026).
    const cambiaStato = async (row, attivo) => {
        const token = getToken();
        if (!token) return { annullato: true };
        const result = await new MyApiClient().updateAdminMoveGoal(token, row.moveGoalId, { active: attivo });
        if (result && result.error) return { error: result.error };
        setForm((f) => (f && f.original ? { ...f, original: { ...f.original, active: attivo } } : f));
        loadData();
        return {};
    };

    const remove = (row) => {
        setConfirmText(`Eliminare "${row.description}"? L'operazione non si annulla.`);
        setOnContinue(() => async () => {
            const token = getToken();
            if (!token) return;
            setLoadingShowed(true);
            const result = await new MyApiClient().deleteAdminMoveGoal(token, row.moveGoalId);
            setLoadingShowed(false);
            if (result && result.error) {
                if (result.status === 409) {
                    return showError('Qualcuno ha gia\' iniziato questo traguardo, quindi non si puo\' eliminare: disattivalo.');
                }
                return showError(result.error);
            }
            closePanel();
            return loadData();
        });
        setConfirmShowed(true);
    };

    const data_column = [
        {
            Header: 'Descrizione',
            Footer: 'Descrizione',
            accessor: 'description',
        },
        {
            Header: 'Metrica',
            Footer: 'Metrica',
            accessor: 'metrica',
        },
        {
            Header: 'Periodo',
            Footer: 'Periodo',
            accessor: 'periodo',
        },
        {
            Header: 'Soglia',
            Footer: 'Soglia',
            accessor: 'soglia',
            disableGlobalFilter: true,
        },
        {
            Header: 'Trofeo',
            Footer: 'Trofeo',
            accessor: 'trofeo',
        },
        {
            Header: 'Raggiunto',
            Footer: 'Raggiunto',
            accessor: 'rewardedCount',
            disableGlobalFilter: true,
            Cell: ({ row }) => (
                <div>
                    {row.original.rewardedCount} volte
                    <small className="d-block">iniziato {row.original.startedCount} volte</small>
                </div>
            ),
        },
        {
            Header: 'Stato',
            Footer: 'Stato',
            accessor: 'stato',
            disableGlobalFilter: true,
            Cell: ({ row }) => (
                <div className="d-flex align-items-center">
                    <i className={`fa fa-circle me-1 ${row.original.active ? 'text-success' : 'text-danger'}`}></i>
                    <div>{row.original.stato}</div>
                </div>
            ),
        },
    ];

    // Metrica, periodo, soglia e trofeo: in creazione, oppure finche' nessuno ha iniziato il traguardo
    const neverStarted = !isNew && form.original?.startedCount === 0;
    const lockedRule = !isNew && (!MOVE_GOALS_MANAGEMENT_ENABLED || !neverStarted);
    const unit = metricOf(form.metric)?.unit;
    // Una metrica non calcolata resta in lista solo per il traguardo che gia' la usa
    const metricsAvailable = METRICS.filter(
        (m) => (isGlobal || !m.global) && (m.calculated !== false || (!isNew && m.value === form.original?.metric)),
    );

    /*
     * Il riquadro di modifica: sotto la riga cliccata, oppure in testa alla tabella per un
     * traguardo nuovo. A sinistra le azioni che erano nel menu della riga, a destra Annulla e Salva.
     */
    const pannello = (
        <div className="sid-occ-panel">
            <h5 className="sid-form-section d-flex align-items-center gap-3">
                {isNew ? 'Nuovo traguardo' : 'Modifica traguardo'}
                {!isNew && MOVE_GOALS_MANAGEMENT_ENABLED && form.original && (
                    <SelettoreStato
                        valore={form.original.active ? 'attivo' : 'non_attivo'}
                        onCambia={(v) => cambiaStato(form.original, v === 'attivo')}
                    />
                )}
            </h5>
            <Row className="g-3">
                <Col md={6}>
                    <Form.Label>Metrica</Form.Label>
                    <Form.Select value={form.metric} disabled={lockedRule} onChange={(e) => updateForm({ metric: e.target.value })}>
                        <option value="">Scegli</option>
                        {metricsAvailable.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={6}>
                    <Form.Label>Periodo</Form.Label>
                    <Form.Select value={form.periodType} disabled={lockedRule} onChange={(e) => updateForm({ periodType: e.target.value })}>
                        <option value="">Scegli</option>
                        {PERIODS.map((p) => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={6}>
                    <Form.Label>Soglia{unit ? ` (${unit})` : ''}</Form.Label>
                    <Form.Control
                        type="number"
                        min="0"
                        step={metricOf(form.metric)?.step ?? '1'}
                        value={form.amount}
                        disabled={lockedRule}
                        onChange={(e) => updateForm({ amount: e.target.value })}
                    />
                </Col>
                <Col md={6}>
                    <Form.Label>Trofeo</Form.Label>
                    <Form.Select value={form.trophyType} disabled={lockedRule} onChange={(e) => updateForm({ trophyType: e.target.value })}>
                        <option value="">Scegli</option>
                        {TROPHIES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={12}>
                    <Form.Label>Descrizione</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={form.description}
                        onChange={(e) => updateForm({ description: e.target.value, descriptionTouched: true })}
                    />
                    <Form.Text className="text-muted">E' il testo che vede l'utente nell'app, per esempio «Registra 15 ore di attività Basket».</Form.Text>
                </Col>
            </Row>
            {!isNew && (
                <div className="alert alert-light mt-3 mb-0">
                    {!MOVE_GOALS_MANAGEMENT_ENABLED
                        ? 'In questo ambiente si puo\' cambiare solo la descrizione.'
                        : neverStarted
                          ? 'Nessuno ha ancora iniziato questo traguardo: puoi cambiare tutto. Quando qualcuno lo iniziera\' si potra\' cambiare solo la descrizione.'
                          : 'Qualcuno ha gia\' iniziato questo traguardo: si puo\' cambiare solo la descrizione. Per cambiare altro disattivalo e creane uno nuovo.'}
                </div>
            )}
            <div className="d-flex justify-content-between align-items-center gap-2 mt-4">
                <div className="d-flex gap-2">
                    {!isNew && MOVE_GOALS_MANAGEMENT_ENABLED && form.original && (
                        <>
                            {form.original.startedCount === 0 && (
                                <Button size="sm" className="btn btn-danger light sid-btn-compact" onClick={() => remove(form.original)}>
                                    Elimina
                                </Button>
                            )}
                        </>
                    )}
                </div>
                <div className="d-flex gap-2">
                    <Button size="sm" className="btn btn-dark light sid-btn-compact" onClick={closePanel}>
                        Annulla
                    </Button>
                    <Button size="sm" className="btn btn-primary sid-btn-compact" onClick={save}>
                        Salva
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Modal show={modalShowed} onHide={() => setModalShowed(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalText}</Modal.Body>
            </Modal>

            <Modal className="fade" show={confirmShowed} centered>
                <Modal.Header>
                    <Modal.Title>Sei sicuro?</Modal.Title>
                    <Button onClick={() => setConfirmShowed(false)} variant="" className="btn-close"></Button>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col className="col-xl-64">{confirmText}</Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-light btn-add" onClick={() => setConfirmShowed(false)}>
                        Chiudi
                    </Button>
                    <Button className="btn btn-primary btn-add" onClick={() => { setConfirmShowed(false); if (onContinue) onContinue(); }}>
                        Continua
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            {isGlobal && (
                <div className="alert alert-danger sid-traguardi-deprecato" role="alert">
                    <strong>DEPRECATO</strong>: questi traguardi non sono più visibili nell'app e andranno eliminati anche da qui.
                </div>
            )}

            <LegendaFitcoin />

            <TabellaLista
                openColumn={'description'}
                openRow={(row) => openEdit(row)}
                rowPanel={(row) => (openId !== null && row.moveGoalId === openId ? pannello : null)}
                topPanel={openId === 'new' ? pannello : null}
                cardTitle={titolo}
                pageList={false}
                hasSearch={true}
                searchPlaceholder={'Cerca per descrizione, metrica o periodo'}
                hasFilter={false}
                filterControl={
                    <div className="sid-chip-filters">
                        <ChipFilter label="Trofeo" options={TROFEO_OPTIONS} value={trofeoFilter} onChange={setTrofeoFilter} multiple />
                        <ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
                    </div>
                }
                hasAddMoreAsModal={MOVE_GOALS_MANAGEMENT_ENABLED}
                labelAddMore={'Nuovo traguardo'}
                modalCallBack={openNew}
                data_column={data_column}
                data_json={filteredData}
                sort_by={'periodo'}
                sort_type={'asc'}
            />

            {onBack && (
                <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                    <span />
                    <button type="button" className="btn btn-dark light" onClick={onBack}>
                        Indietro
                    </button>
                </div>
            )}
        </>
    );
};

/*
 * Vecchia rotta .../discipline/:code/traguardi. Per una disciplina porta alla sua scheda
 * con il tab Traguardi aperto; per GLOBAL (traguardi di tutte le discipline, che non
 * hanno una scheda) mostra la tabella qui.
 */
export const TraguardiDisciplina = () => {
    const { code } = useParams();
    const navigate = useNavigate();

    if (code !== GLOBAL) {
        return <Navigate to={`/superadmin/anagrafiche/discipline/${code}?tab=traguardi`} replace />;
    }
    return <TraguardiTabella code={code} onBack={() => navigate('/superadmin/anagrafiche/discipline')} />;
};

export default TraguardiDisciplina;
